import { calculateHorizonApi } from '@core/core/api'
import type {
  AlgorithmParams,
  AlgorithmTypes,
  Calculation,
  FaultsSeparationParams,
  FormDataML,
  MLModalNavItem,
  MLModalNavItemType,
  SeismophationsParams,
  StickSeparationParams,
} from '@core/core/types'
import { hasKeysOfUnknown } from 'common/helpers'
import type { ProjectId } from 'common/types'

import {
  algorithmTypeItems,
  defaultFaultsSeparationData,
  defaultHorizonExtensionData,
  defaultSeismophationsData,
  defaultStickSeparationData,
  FIRST_CALCULATION_ID,
  mlItems,
} from './constants'

export function getMenuItems(arr: (string | number)[]) {
  return arr.map(i => {
    const el = typeof i === 'number' ? `${i}` : i
    return { id: el, label: el }
  })
}

export function getDefaultFormData(defaultAlgorithmType: AlgorithmTypes): FormDataML {
  switch (defaultAlgorithmType) {
    case 'horizonsExtension':
      return defaultHorizonExtensionData
    case 'faultsSeparation':
      return defaultFaultsSeparationData
    case 'seismophations':
      return defaultSeismophationsData
    case 'stickSeparation':
      return defaultStickSeparationData
  }
}

export const getFullMenuItems = (currentAlgorithm: AlgorithmTypes): MLModalNavItem[] => {
  const customData: Partial<Record<MLModalNavItemType, string>> = {
    algorithm: getAlgorithmName(currentAlgorithm),
  }

  return mlItems.map(item => ({ ...item, additionalText: customData[item.id] }))
}

function getAlgorithmName(type: AlgorithmTypes) {
  return algorithmTypeItems.find(i => i.id === type)?.label
}

function validateAlgorithm(algorithmParams: AlgorithmParams) {
  return isAllFilled(algorithmParams)
}

function validateCalculations(calculations: Calculation[]) {
  const cubeName = calculations.find(calculation => calculation.id === FIRST_CALCULATION_ID)?.params
    ?.seismicSurvey.entity?.entityName

  return (
    calculations.some(calc => calc.params?.seismicSurvey.entity?.entityId) &&
    calculations.every(
      calc =>
        calc.params &&
        'horizon' in calc.params &&
        calc.params.horizon.entity?.entityId &&
        calc.params.horizon.entity.entityName === cubeName,
    )
  )
}

export function validateForm(form: FormDataML) {
  return validateAlgorithm(form.algorithm) && validateCalculations(form.calculations)
}

function isAllFilled(algorithmParams: AlgorithmParams) {
  return Object.values(algorithmParams).every(value => value != null && value !== '')
}

export function executeCalculations(formData: FormDataML, projectId: ProjectId) {
  let calculator
  switch (formData.type) {
    case 'horizonsExtension':
      calculator = executeHorizonCalculations
      break
    case 'faultsSeparation':
      break
    case 'seismophations':
      break
    case 'stickSeparation':
      break
  }

  if (!calculator) {
    console.log(`Не найдена функция для расчета ${formData.type}`)
    return
  }

  return Promise.all(calculator(formData, projectId)).catch(err => {
    throw err
  })
}

function executeHorizonCalculations(formData: FormDataML, projectId: ProjectId) {
  return formData.calculations.map(calc => calculateHorizonApi(calc, formData, projectId))
}

function isCommonParams(obj: AlgorithmParams) {
  return hasKeysOfUnknown(obj, 'cropSize', 'iterations', 'direction', 'overlap', 'aggregation')
}

export function isFaultsSeparationParams(
  obj: AlgorithmParams,
): asserts obj is FaultsSeparationParams {
  if (isCommonParams(obj) && 'stickSelection' in obj && 'numberOfLeafs' in obj) return

  throw new Error('Объект не FaultsSeparationParams')
}

export function isSeismophationsParams(obj: AlgorithmParams): asserts obj is SeismophationsParams {
  if (isCommonParams(obj) && 'additionalTraining' in obj) return

  throw new Error('Объект не SeismophationsParams')
}

export function isStickSeparationParams(
  obj: AlgorithmParams,
): asserts obj is StickSeparationParams {
  if ('numberOfLeafs' in obj) return

  throw new Error('Объект не StickSeparationParams')
}
